import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponseEnvelope } from '@doctor-scheduling/shared/models';
import {
  CalendarIntegrationStatus,
  CalendarProvider,
} from '@doctor-scheduling/shared/constants';
import { API_BASE_URL } from './api-base-url.token';

/** External calendar row returned by integration API. */
export interface ExternalCalendarListItem {
  id: string;
  integrationId: string;
  externalCalendarId: string;
  name: string;
  timezone: string;
  isPrimary: boolean;
  isEnabled: boolean;
}

/** Calendar integration summary (tokens never exposed). */
export interface CalendarIntegrationListItem {
  id: string;
  doctorId: string;
  clinicId: string;
  provider: CalendarProvider;
  email: string;
  status: CalendarIntegrationStatus;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  externalCalendars: ExternalCalendarListItem[];
}

/** External busy / calendar event block returned by the busy API. */
export interface ExternalBusyBlock {
  startTime: string;
  endTime: string;
  provider: string;
  title: string;
  description: string | null;
  location: string | null;
  externalEventId: string | null;
}

/** Operator-facing busy fetch diagnostics returned by the busy API. */
export interface CalendarBusyDiagnostics {
  reason: string;
  integrations: Array<{
    id: string;
    provider: string;
    status: string;
    email: string | null;
    enabledCalendarIds: string[];
    lastError: string | null;
  }>;
  activeIntegrationCount: number;
  enabledCalendarCount: number;
  timeZone: string | null;
  graphStartLocal: string | null;
  graphEndLocal: string | null;
  providerErrors: Array<{
    provider: string;
    status: string;
    error: string;
  }>;
  rawPeriodCount: number;
}

/** Busy list payload with optional diagnostics (new API shape). */
export interface ExternalBusyListResponse {
  blocks: ExternalBusyBlock[];
  diagnostics: CalendarBusyDiagnostics;
}

/**
 * Normalizes busy API data (legacy array or `{ blocks, diagnostics }` envelope).
 * @param data - Raw `data` field from the busy API response
 */
export function normalizeBusyPeriodsData(
  data: ExternalBusyBlock[] | ExternalBusyListResponse | null | undefined
): ExternalBusyListResponse {
  if (!data) {
    return {
      blocks: [],
      diagnostics: {
        reason: 'empty_response',
        integrations: [],
        activeIntegrationCount: 0,
        enabledCalendarCount: 0,
        timeZone: null,
        graphStartLocal: null,
        graphEndLocal: null,
        providerErrors: [],
        rawPeriodCount: 0,
      },
    };
  }
  if (Array.isArray(data)) {
    return {
      blocks: data,
      diagnostics: {
        reason: 'legacy_api_response',
        integrations: [],
        activeIntegrationCount: 0,
        enabledCalendarCount: 0,
        timeZone: null,
        graphStartLocal: null,
        graphEndLocal: null,
        providerErrors: [],
        rawPeriodCount: data.length,
      },
    };
  }
  return {
    blocks: data.blocks ?? [],
    diagnostics: data.diagnostics ?? {
      reason: 'missing_diagnostics',
      integrations: [],
      activeIntegrationCount: 0,
      enabledCalendarCount: 0,
      timeZone: null,
      graphStartLocal: null,
      graphEndLocal: null,
      providerErrors: [],
      rawPeriodCount: data.blocks?.length ?? 0,
    },
  };
}

/** Google Calendar booking page / appointment schedule detected on a connected calendar. */
export interface GoogleBookingScheduleItem {
  title: string;
  bookingUrl?: string | null;
  typicalStartTime?: string;
  typicalEndTime?: string;
  timeZone: string;
}

/** HTTP client for doctor calendar integration endpoints. */
@Injectable({ providedIn: 'root' })
export class CalendarIntegrationApi {
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);

  /** Base URL for calendar integration routes under `/api/v1/calendar-integrations`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/calendar-integrations`;

  /** Angular HttpClient for REST calls. */
  private readonly http = inject(HttpClient);

  /**
   * Lists calendar integrations for a doctor.
   * @param doctorId - Doctor id
   */
  listIntegrations(
    doctorId: string
  ): Observable<ApiResponseEnvelope<CalendarIntegrationListItem[]>> {
    const params = new HttpParams().set('doctorId', doctorId);
    return this.http.get<ApiResponseEnvelope<CalendarIntegrationListItem[]>>(
      this.baseUrl,
      { params }
    );
  }

  /**
   * Lists external busy periods for a doctor on a local calendar day.
   * @param doctorId - Doctor id
   * @param date - YYYY-MM-DD
   */
  listBusyPeriods(
    doctorId: string,
    date: string,
    timezone?: string,
    fresh = false
  ): Observable<ApiResponseEnvelope<ExternalBusyListResponse | ExternalBusyBlock[]>> {
    let params = new HttpParams().set('date', date);
    if (timezone) {
      params = params.set('timezone', timezone);
    }
    if (fresh) {
      params = params.set('fresh', 'true');
    }
    return this.http.get<
      ApiResponseEnvelope<ExternalBusyListResponse | ExternalBusyBlock[]>
    >(`${this.baseUrl}/${doctorId}/busy`, { params });
  }

  /**
   * Lists Google Calendar booking pages used to drive available appointment slots.
   * @param doctorId - Doctor id
   */
  listGoogleBookingSchedules(
    doctorId: string
  ): Observable<ApiResponseEnvelope<GoogleBookingScheduleItem[]>> {
    return this.http.get<ApiResponseEnvelope<GoogleBookingScheduleItem[]>>(
      `${this.baseUrl}/${doctorId}/google/booking-schedules`
    );
  }

  /**
   * Fetches Google OAuth URL then redirects the browser (requires Authorization header).
   * @param doctorId - Doctor to connect
   */
  connectGoogle(doctorId: string): Observable<ApiResponseEnvelope<{ url: string }>> {
    return this.http.get<ApiResponseEnvelope<{ url: string }>>(
      `${this.baseUrl}/${doctorId}/google/connect-url`
    );
  }

  /**
   * Fetches Outlook OAuth URL then redirects the browser (requires Authorization header).
   * @param doctorId - Doctor to connect
   */
  connectOutlook(doctorId: string): Observable<ApiResponseEnvelope<{ url: string }>> {
    return this.http.get<ApiResponseEnvelope<{ url: string }>>(
      `${this.baseUrl}/${doctorId}/outlook/connect-url`
    );
  }

  /**
   * @deprecated Use connectGoogle() which returns URL via authenticated API.
   */
  connectGoogleRedirect(doctorId: string): void {
    window.location.href = `${this.baseUrl}/${doctorId}/google/connect`;
  }

  /**
   * @deprecated Use connectOutlook() which returns URL via authenticated API.
   */
  connectOutlookRedirect(doctorId: string): void {
    window.location.href = `${this.baseUrl}/${doctorId}/outlook/connect`;
  }

  /**
   * Refreshes and lists external calendars for a provider.
   * @param doctorId - Doctor id
   * @param provider - GOOGLE or OUTLOOK
   */
  listCalendars(
    doctorId: string,
    provider: CalendarProvider
  ): Observable<ApiResponseEnvelope<ExternalCalendarListItem[]>> {
    return this.http.get<ApiResponseEnvelope<ExternalCalendarListItem[]>>(
      `${this.baseUrl}/${doctorId}/${provider}/calendars`
    );
  }

  /**
   * Selects the enabled calendar for availability sync.
   * @param doctorId - Doctor id
   * @param provider - GOOGLE or OUTLOOK
   * @param externalCalendarId - Provider calendar id
   * @param isEnabled - Whether to enable this calendar
   */
  selectCalendar(
    doctorId: string,
    provider: CalendarProvider,
    externalCalendarId: string,
    isEnabled = true
  ): Observable<ApiResponseEnvelope<ExternalCalendarListItem[]>> {
    return this.http.put<ApiResponseEnvelope<ExternalCalendarListItem[]>>(
      `${this.baseUrl}/${doctorId}/${provider}/calendars/select`,
      { externalCalendarId, isEnabled }
    );
  }

  /**
   * Disconnects a calendar provider for a doctor.
   * @param doctorId - Doctor id
   * @param provider - GOOGLE or OUTLOOK
   */
  disconnect(
    doctorId: string,
    provider: CalendarProvider
  ): Observable<ApiResponseEnvelope<{ success: boolean }>> {
    return this.http.delete<ApiResponseEnvelope<{ success: boolean }>>(
      `${this.baseUrl}/${doctorId}/${provider}`
    );
  }

  /**
   * Enqueues a manual sync job for a provider integration.
   * @param doctorId - Doctor id
   * @param provider - GOOGLE or OUTLOOK
   */
  syncNow(
    doctorId: string,
    provider: CalendarProvider
  ): Observable<ApiResponseEnvelope<{ success: boolean; queued: boolean }>> {
    return this.http.post<
      ApiResponseEnvelope<{ success: boolean; queued: boolean }>
    >(`${this.baseUrl}/${doctorId}/${provider}/sync`, {});
  }
}
